import { useCallback, useRef } from 'react';
import CameraOrbitControls from './CameraOrbitControls';

const TransformModelsPanel = ({
  showTransformModelsPanel,
  transformModelsPanelCollapsed,
  setTransformModelsPanelCollapsed,
  onClose,
  onCancelPreview,
  onGenerate,
  onApply,
  generateDisabled = false,
  generateLoading = false,
  applyDisabled = false,
  applyLoading = false,
  outputModelSrc = '',
  outputModelName = '',
}) => {
  const cameraParamsRef = useRef({
    azimuth: 0,
    elevation: 0,
    distance: 1.0,
  });

  const handleCameraChange = useCallback((params) => {
    cameraParamsRef.current = params;
  }, []);

  const hasOutputPreview = Boolean(outputModelSrc?.trim());

  const handleCloseOrCancelPreview = () => {
    if (hasOutputPreview) {
      onCancelPreview?.();
    } else {
      onClose?.();
    }
  };

  const handleGenerateClick = () => {
    if (generateDisabled || generateLoading) return;
    onGenerate?.(cameraParamsRef.current);
  };

  if (!showTransformModelsPanel) return null;

  return (
    <div className="flex shrink-0 min-h-0 items-stretch">
      <button
        type="button"
        onClick={() => setTransformModelsPanelCollapsed((v) => !v)}
        className="flex w-6 h-12 shrink-0 flex-col items-center justify-center self-center rounded-l-lg transition-colors bg-white/35 box-border backdrop-blur-xl backdrop-saturate-150 border-r border-gray-200/60"
        title={transformModelsPanelCollapsed ? 'Show transform models panel' : 'Hide transform models panel'}
        aria-expanded={!transformModelsPanelCollapsed}
        aria-label={transformModelsPanelCollapsed ? 'Show transform models panel' : 'Hide transform models panel'}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`shrink-0 transition-transform duration-300 ease-out ${transformModelsPanelCollapsed ? '' : 'rotate-180'}`}
          aria-hidden
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <aside
        className={`shrink-0 min-h-0 overflow-hidden transition-[width] duration-300 ease-in-out motion-reduce:transition-none ${
          transformModelsPanelCollapsed ? 'w-0 pointer-events-none' : 'w-[16rem]'
        }`}
        aria-label="Transform Models"
      >
        <div className="flex h-full min-h-0 w-[16] min-w-[16rem] flex-col gap-4 border-l border-gray-200 border-white/50 bg-white/35 py-4 px-2 box-border backdrop-blur-xl backdrop-saturate-150">
          <div className="flex shrink-0 items-center justify-between gap-2">
            <h3 className="text-sm lao-mn text-gray-700">Transform Models</h3>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            >
              Close
            </button>
          </div>

          <div className="">
            <div className="rounded-3xl border border-gray-200 bg-white/70 p-2 h-[180px] flex touch-none select-none items-center justify-center overflow-hidden">
              {outputModelSrc ? (
                <img
                  src={outputModelSrc}
                  alt={outputModelName || 'Output model'}
                  className="max-h-full max-w-full object-contain"
                  draggable={false}
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="px-3 text-center text-xs text-gray-500">
                  {generateDisabled
                    ? 'Select a model on the canvas'
                    : 'Generated preview will appear here'}
                </span>
              )}
            </div>
            <span className="block text-sm font-semibold text-gray-700 mt-1 text-center">
              Output Model
            </span>
          </div>

          <CameraOrbitControls onCameraChange={handleCameraChange} />

          <div className="mt-auto flex flex-col shrink-0 items-center gap-2 px-0.5 pt-1">
            <button
              type="button"
              onClick={handleGenerateClick}
              disabled={generateDisabled || generateLoading}
              className="flex flex-row justify-center items-center gap-2 w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 disabled:hover:text-primary-300"
            >
              <svg className="w-4 h-4" viewBox="0 0 15 17" fill="currentColor" aria-hidden="true">
                <path d="M5.25928 2.586C5.70778 1.2735 7.52128 1.23375 8.05303 2.46675L8.09803 2.58675L8.70328 4.35675C8.84199 4.76267 9.06614 5.13413 9.36061 5.44607C9.65507 5.758 10.013 6.00316 10.4103 6.165L10.573 6.22575L12.343 6.83025C13.6555 7.27875 13.6953 9.09225 12.463 9.624L12.343 9.669L10.573 10.2743C10.167 10.4129 9.79537 10.637 9.4833 10.9314C9.17123 11.2259 8.92596 11.5839 8.76403 11.9813L8.70328 12.1432L8.09878 13.914C7.65028 15.2265 5.83678 15.2662 5.30578 14.034L5.25928 13.914L4.65478 12.144C4.51617 11.7379 4.29206 11.3663 3.99759 11.0543C3.70312 10.7422 3.34513 10.4969 2.94778 10.335L2.78578 10.2743L1.01578 9.66975C-0.297467 9.22125 -0.337217 7.40775 0.895783 6.87675L1.01578 6.83025L2.78578 6.22575C3.19171 6.08704 3.56317 5.8629 3.8751 5.56843C4.18704 5.27396 4.43219 4.91602 4.59403 4.51875L4.65478 4.35675L5.25928 2.586ZM6.67903 3.0705L6.07453 4.8405C5.86333 5.45948 5.51973 6.02498 5.06769 6.49762C4.61564 6.97027 4.06599 7.33869 3.45703 7.57725L3.26953 7.6455L1.49953 8.25L3.26953 8.8545C3.88851 9.06571 4.45401 9.4093 4.92666 9.86135C5.3993 10.3134 5.76773 10.863 6.00628 11.472L6.07453 11.6595L6.67903 13.4295L7.28353 11.6595C7.49474 11.0405 7.83833 10.475 8.29038 10.0024C8.74243 9.52973 9.29207 9.16131 9.90103 8.92275L10.0885 8.85525L11.8585 8.25L10.0885 7.6455C9.46956 7.43429 8.90406 7.0907 8.43141 6.63865C7.95877 6.1866 7.59034 5.63696 7.35178 5.028L7.28428 4.8405L6.67903 3.0705ZM12.679 1.35516e-07C12.8193 -1.76979e-07 12.9568 0.0393587 13.0759 0.113604C13.195 0.187849 13.2908 0.294002 13.3525 0.42L13.3885 0.50775L13.651 1.27725L14.4213 1.53975C14.5619 1.58752 14.6852 1.67596 14.7755 1.79386C14.8658 1.91176 14.919 2.05381 14.9285 2.20202C14.938 2.35022 14.9032 2.4979 14.8287 2.62634C14.7542 2.75478 14.6432 2.8582 14.5098 2.9235L14.4213 2.9595L13.6518 3.222L13.3893 3.99225C13.3414 4.13282 13.2529 4.25602 13.135 4.34624C13.017 4.43647 12.875 4.48964 12.7268 4.49904C12.5786 4.50844 12.4309 4.47363 12.3025 4.39903C12.1741 4.32443 12.0708 4.2134 12.0055 4.08L11.9695 3.99225L11.707 3.22275L10.9368 2.96025C10.7962 2.91248 10.6729 2.82404 10.5826 2.70614C10.4923 2.58824 10.4391 2.44619 10.4296 2.29798C10.4201 2.14978 10.4548 2.0021 10.5294 1.87366C10.6039 1.74522 10.7149 1.6418 10.8483 1.5765L10.9368 1.5405L11.7063 1.278L11.9688 0.50775C12.0194 0.359569 12.115 0.230931 12.2424 0.139874C12.3698 0.0488168 12.5225 -9.3961e-05 12.679 1.35516e-07Z" />
              </svg>
              {generateLoading ? 'Generating…' : 'Generate'}
            </button>

            <div className="flex flex-row gap-2 w-full">
              <button
                type="button"
                onClick={() => onApply?.()}
                disabled={applyDisabled || generateLoading || applyLoading}
                className="flex flex-row justify-center items-center gap-2 w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 disabled:hover:text-primary-300"
              >
                <svg className="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                  <path d="M12.5625 6.5625C12.5625 7.35043 12.4073 8.13065 12.1058 8.8586C11.8043 9.58656 11.3623 10.248 10.8051 10.8051C10.248 11.3623 9.58656 11.8043 8.8586 12.1058C8.13065 12.4073 7.35043 12.5625 6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53845 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58656 1.01922 8.8586C0.717695 8.13065 0.5625 7.35043 0.5625 6.5625C0.5625 4.9712 1.19464 3.44508 2.31986 2.31986C3.44508 1.19464 4.9712 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625Z" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M4.34033 6.98445L5.34367 8.13178C5.40969 8.20734 5.49192 8.26701 5.58422 8.30636C5.67652 8.34571 5.77651 8.36372 5.87674 8.35904C5.97697 8.35436 6.07485 8.32712 6.16308 8.27934C6.25132 8.23157 6.32763 8.16449 6.38633 8.08312L8.78433 4.76245" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {applyLoading ? 'Applying…' : 'Apply'}
              </button>
              <button
                type="button"
                onClick={handleCloseOrCancelPreview}
                disabled={generateLoading || applyLoading}
                title={hasOutputPreview ? 'Discard generated preview' : 'Close transform panel'}
                aria-label={hasOutputPreview ? 'Cancel generated preview' : 'Close transform panel'}
                className="flex flex-row justify-center items-center gap-2 w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 disabled:hover:text-primary-300"
              >
                {hasOutputPreview ? (
                  <svg className="w-4 h-4" viewBox="0 0 14 14" fill="none" stroke="currentColor" aria-hidden="true">
                    <path d="M8.5625 8.5625L4.5625 4.5625M8.5625 4.5625L4.5625 8.5625M6.5625 12.5625C5.77457 12.5625 4.99435 12.4073 4.2664 12.1058C3.53844 11.8043 2.87701 11.3623 2.31986 10.8051C1.76271 10.248 1.32075 9.58655 1.01922 8.8586C0.717693 8.13065 0.562499 7.35043 0.562499 6.5625C0.562499 5.77457 0.717693 4.99435 1.01922 4.2664C1.32075 3.53845 1.76271 2.87701 2.31986 2.31986C2.87701 1.76271 3.53844 1.32075 4.2664 1.01922C4.99435 0.717694 5.77457 0.5625 6.5625 0.5625C8.1538 0.5625 9.67992 1.19464 10.8051 2.31986C11.9304 3.44508 12.5625 4.9712 12.5625 6.5625C12.5625 8.1538 11.9304 9.67992 10.8051 10.8051C9.67992 11.9304 8.1538 12.5625 6.5625 12.5625Z" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : null}
                {hasOutputPreview ? 'Cancel' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default TransformModelsPanel;
